import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { PasswordRules } from '@/components/auth/password-rules';
import { editorStore } from '@/context/store/store';
import { setForceChangePassword } from '@/context/store/slices/authSlice';
import { getApiErrorMessage, getHttpStatus } from '@/lib/api-error';
import {
  forceChangePasswordDefaultValues,
  forceChangePasswordSchema,
  type ForceChangePasswordFormValues,
} from '@/lib/auth-schemas';
import {
  readForceChangeContext,
} from '@/lib/force-change-storage';
import { useForceChangePasswordMutation } from '@/query/auth';
import '@/styles/route-auth.scss';

export const Route = createFileRoute('/force-change-password')({
  beforeLoad: () => {
    const state = editorStore.getState().auth;
    if (state.status === 'force-change-password') return;

    const stored = readForceChangeContext();
    if (stored) {
      editorStore.dispatch(setForceChangePassword(stored));
      return;
    }

    throw redirect({ to: '/login' });
  },
  component: ForceChangePasswordScreen,
});

function ForceChangePasswordScreen() {
  const mutation = useForceChangePasswordMutation();
  const [showPwd, setShowPwd] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = React.useState(false);
  const [sessionExpired, setSessionExpired] = React.useState(false);

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isValid },
  } = useForm<ForceChangePasswordFormValues>({
    resolver: zodResolver(forceChangePasswordSchema),
    defaultValues: forceChangePasswordDefaultValues,
    mode: 'onChange',
  });

  const newPasswordValue = useWatch({
    control,
    name: 'newPassword',
    defaultValue: '',
  });

  const onSubmit = async (data: ForceChangePasswordFormValues) => {
    if (mutation.isPending) return;
    setSessionExpired(false);
    try {
      await mutation.mutateAsync({ newPassword: data.newPassword });
    } catch (error) {
      if (getHttpStatus(error) === 400) {
        setSessionExpired(true);
        setError('newPassword', {
          message: 'Your session has expired. Please sign in again.',
        });
        return;
      }
    }
  };

  const errorMessage =
    mutation.isError && !errors.newPassword && !sessionExpired
      ? getApiErrorMessage(
          mutation.error,
          'Could not save your password. Please try again.',
        )
      : null;

  return (
    <main className="auth">
      <div className="auth__brand">World Athletics · Book of Rules</div>

      <div className="auth__card">
        <h1 className="auth__heading">Set your password.</h1>
        <p className="auth__subtitle">
          This is your first sign-in. Choose a password you&apos;ll use next
          time you log in.
        </p>

        <form
          className="auth__form"
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
        >
          <div
            className={`auth__field${errors.newPassword ? ' auth__field--invalid' : ''}`}
          >
            <input
              id="fcp-new-password"
              className="auth__field-input"
              type={showPwd ? 'text' : 'password'}
              placeholder=" "
              autoComplete="new-password"
              spellCheck={false}
              {...register('newPassword', {
                onBlur: () => {
                  setIsPasswordFocused(false);
                },
              })}
              onFocus={() => {
                setIsPasswordFocused(true);
              }}
            />
            <label className="auth__field-label" htmlFor="fcp-new-password">
              New password
            </label>
            <button
              type="button"
              className="auth__field-toggle"
              onClick={() => {
                setShowPwd(s => !s);
              }}
              aria-label={showPwd ? 'Hide password' : 'Show password'}
              aria-pressed={showPwd}
            >
              {showPwd ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
            </button>
            {errors.newPassword && (
              <p className="auth__field-error">{errors.newPassword.message}</p>
            )}
          </div>

          {isPasswordFocused && newPasswordValue.length > 0 && (
            <PasswordRules password={newPasswordValue} />
          )}

          <div
            className={`auth__field${errors.confirmNewPassword ? ' auth__field--invalid' : ''}`}
          >
            <input
              id="fcp-confirm-password"
              className="auth__field-input"
              type={showConfirm ? 'text' : 'password'}
              placeholder=" "
              autoComplete="new-password"
              spellCheck={false}
              {...register('confirmNewPassword')}
            />
            <label
              className="auth__field-label"
              htmlFor="fcp-confirm-password"
            >
              Confirm password
            </label>
            <button
              type="button"
              className="auth__field-toggle"
              onClick={() => {
                setShowConfirm(s => !s);
              }}
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              aria-pressed={showConfirm}
            >
              {showConfirm ? (
                <EyeOffIcon size={18} />
              ) : (
                <EyeIcon size={18} />
              )}
            </button>
            {errors.confirmNewPassword && (
              <p className="auth__field-error">
                {errors.confirmNewPassword.message}
              </p>
            )}
          </div>

          {sessionExpired && (
            <p className="auth__field-error">
              <Link to="/login">Return to sign in</Link>
            </p>
          )}

          {errorMessage && (
            <div role="alert" className="auth__error">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="auth__submit"
            disabled={!isValid || mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <span className="auth__submit-spinner" aria-hidden="true" />
                Saving…
              </>
            ) : (
              'Save and continue'
            )}
          </button>
        </form>
      </div>

      <div className="auth__footer">© World Athletics</div>
    </main>
  );
}
